import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

import { Logger } from '@/lib/logs/logger';

import { ExcelState, PanelState } from '../types';
import { extractServerError, NONE_COLUMN, UNKNOWN_ERROR_KEY } from '../utils';
import { useAnalyzeDraftPlotSpreadsheet } from './useDraftPlotUploadActions';

type UseSpreadsheetDropzoneParams = {
  panelState: PanelState;
  excelState: ExcelState | null;
  onAnalyzeSuccess: (state: ExcelState) => void;
  setAnalyzeError: (message: string | undefined) => void;
  t: TFunction;
};

export const useSpreadsheetDropzone = ({
  panelState,
  excelState,
  onAnalyzeSuccess,
  setAnalyzeError,
  t,
}: UseSpreadsheetDropzoneParams) => {
  const { submit, isSubmitting: isAnalyzing } = useAnalyzeDraftPlotSpreadsheet();

  const dropzone = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 1,
    disabled: panelState !== 'excel' || isAnalyzing || !!excelState,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setAnalyzeError(undefined);

      try {
        const result = await submit({ file });
        const firstSheet = result?.sheet_names[0] ?? '';
        const columns = result?.columns_by_sheet[`${firstSheet}`] ?? result?.columns_by_sheet[''] ?? [];

        onAnalyzeSuccess({
          fileId: result?.file_id ?? '',
          sheetNames: result?.sheet_names ?? [],
          columnsBySheet: result?.columns_by_sheet ?? {},
          selectedSheet: firstSheet,
          latColumn: columns[0] ?? '',
          lngColumn: columns[1] ?? '',
          areaColumn: NONE_COLUMN,
          areaUnits: 'square_meters',
          nameColumn: NONE_COLUMN,
          landType: '',
        });
      } catch (error) {
        setAnalyzeError(extractServerError(error, t(UNKNOWN_ERROR_KEY)));
        Logger.errorOnce(error as Error);
      }
    },
  });

  const fileErrors = useMemo(
    () =>
      Array.from(
        new Set(
          dropzone.fileRejections
            .flatMap((rejection) => rejection.errors)
            .map((error) => {
              if (error.code === 'file-invalid-type') return t('shared.uploadPlots.error.file-invalid-type');
              if (error.code === 'file-too-large') return t('shared.uploadPlots.error.file-too-large');
              if (error.code === 'too-many-files') return t('shared.uploadPlots.error.too-many-files');
              return t(UNKNOWN_ERROR_KEY);
            }),
        ),
      ),
    [dropzone.fileRejections, t],
  );

  return {
    ...dropzone,
    fileErrors,
    isAnalyzing,
  };
};
